/**
 * variant-sale-features.js
 * Michelangelo Intimo - Variant Sale Features & Preselection
 * Handles:
 * 1. Global event delegation for .variant-sale-chip clicks
 * 2. Perfect synchronization of option inputs, badges, and active chip states
 * 3. Listening to on:variant:change events from Symmetry
 * 4. MutationObserver to automatically re-sync whenever Symmetry swaps DOM nodes
 */
(function() {
  if (window._variantSaleFeaturesInitialized) return;
  window._variantSaleFeaturesInitialized = true;

  function getVariantsData(scope) {
    const dataEl = (scope || document).querySelector('.js-variant-sale-data');
    if (!dataEl) return [];
    try {
      return JSON.parse(dataEl.textContent);
    } catch (e) {
      return [];
    }
  }

  function getSelectedOptionValue(picker, index) {
    const selector = picker.querySelector(`.option-selector[data-index="${index}"]`);
    if (!selector) return null;
    if (selector.dataset.selectorType === 'dropdown') {
      const activeOpt = selector.querySelector('.custom-select__option[aria-selected="true"]');
      return activeOpt ? activeOpt.dataset.value : null;
    } else {
      const checkedRadio = selector.querySelector('input.opt-btn:checked');
      return checkedRadio ? checkedRadio.value : null;
    }
  }

  // Update dynamic option badges (Color: Sale badge / Size: -X% badge)
  function updateBadges(picker) {
    if (!picker) picker = document.querySelector('variant-picker');
    if (!picker) return;

    const variantsData = getVariantsData(picker);
    if (!variantsData || variantsData.length === 0) return;

    const optionCount = picker.querySelectorAll('.option-selector').length;

    // 1. Color option (Index 1) Sale Badges
    const opt1Selector = picker.querySelector('.option-selector[data-index="1"]');
    if (opt1Selector) {
      const opt1Badges = opt1Selector.querySelectorAll('.opt-badge--sale');
      opt1Badges.forEach(badge => {
        const colorVal = badge.dataset.badgeFor;
        const hasSale = variantsData.some(v => {
          const vColor = v.opt1 || v.option1;
          return vColor === colorVal && v.compare_at_price > v.price && v.available;
        });
        badge.style.display = hasSale ? '' : 'none';
      });
    }

    // 2. Size option (Index 2) Discount Badges
    if (optionCount >= 2) {
      const selectedColor = getSelectedOptionValue(picker, 1);
      const opt2Selector = picker.querySelector('.option-selector[data-index="2"]');
      if (!opt2Selector) return;

      const opt2Badges = opt2Selector.querySelectorAll('.opt-badge--discount');
      opt2Badges.forEach(badge => {
        const sizeVal = badge.dataset.badgeFor;
        const matchingVariant = variantsData.find(v => {
          const vColor = v.opt1 || v.option1;
          const vSize = v.opt2 || v.option2;
          if (selectedColor) {
            return vColor === selectedColor && vSize === sizeVal;
          }
          return vSize === sizeVal;
        });

        if (matchingVariant && matchingVariant.compare_at_price > matchingVariant.price && matchingVariant.available) {
          const diff = (matchingVariant.compare_at_price - matchingVariant.price) * 100.0;
          const pct = Math.round(diff / matchingVariant.compare_at_price);
          badge.textContent = `-${pct}%`;
          badge.style.display = '';
        } else {
          badge.style.display = 'none';
        }
      });
    }
  }

  // Update active state on info-box chips
  function updateChipsState(picker, currentVariantId) {
    const chips = document.querySelectorAll('.variant-sale-chip');
    if (!chips.length) return;

    if (!picker) picker = document.querySelector('variant-picker');

    let targetId = currentVariantId;
    if (!targetId && picker) {
      if (picker.variant && picker.variant.id) {
        targetId = picker.variant.id;
      } else {
        const idInput = picker.closest('.section-main-product')?.querySelector('input[name="id"]') ||
                        document.querySelector('form[action*="/cart/add"] input[name="id"]');
        if (idInput) targetId = idInput.value;
      }
    }

    const curOpt1 = picker ? getSelectedOptionValue(picker, 1) : null;
    const curOpt2 = picker ? getSelectedOptionValue(picker, 2) : null;
    const curOpt3 = picker ? getSelectedOptionValue(picker, 3) : null;

    chips.forEach(chip => {
      let isMatch = false;
      if (targetId && chip.dataset.variantId === String(targetId)) {
        isMatch = true;
      } else if (curOpt1 && chip.dataset.opt1 === curOpt1) {
        if (!chip.dataset.opt2 || chip.dataset.opt2 === curOpt2) {
          if (!chip.dataset.opt3 || chip.dataset.opt3 === curOpt3) {
            isMatch = true;
          }
        }
      }
      chip.classList.toggle('is-active', isMatch);
    });
  }

  // 1. GLOBAL CLICK DELEGATION for Chips (handles clicks and triggers Symmetry variant change)
  document.addEventListener('click', function(e) {
    const chip = e.target.closest('.variant-sale-chip');
    if (!chip) return;
    e.preventDefault();

    const picker = chip.closest('variant-picker') ||
                   chip.closest('.section-main-product')?.querySelector('variant-picker') ||
                   document.querySelector('variant-picker');
    if (!picker) return;

    const opt1 = chip.dataset.opt1;
    const opt2 = chip.dataset.opt2;
    const opt3 = chip.dataset.opt3;
    const variantId = chip.dataset.variantId;

    // Immediately toggle active state on chips
    document.querySelectorAll('.variant-sale-chip').forEach(c => {
      c.classList.toggle('is-active', c === chip);
    });

    const opts = [
      { index: 1, val: opt1 },
      { index: 2, val: opt2 },
      { index: 3, val: opt3 }
    ].filter(o => o.val);

    let lastRadio = null;

    opts.forEach(opt => {
      const selector = picker.querySelector(`.option-selector[data-index="${opt.index}"]`);
      if (!selector) return;

      if (selector.dataset.selectorType === 'dropdown') {
        let optEl = selector.querySelector(`.custom-select__option[data-value="${CSS.escape(opt.val)}"]`);
        if (!optEl) {
          selector.querySelectorAll('.custom-select__option').forEach(o => {
            if (o.dataset.value && o.dataset.value.trim().toLowerCase() === opt.val.trim().toLowerCase()) {
              optEl = o;
            }
          });
        }
        if (optEl) {
          const cs = optEl.closest('custom-select');
          if (cs && typeof cs.selectOption === 'function') {
            cs.selectOption(optEl);
          }
        }
      } else {
        const radios = selector.querySelectorAll('input.opt-btn');
        let radio = selector.querySelector(`input.opt-btn[value="${CSS.escape(opt.val)}"]`);
        if (!radio) {
          radios.forEach(r => {
            if (r.value.trim().toLowerCase() === opt.val.trim().toLowerCase()) {
              radio = r;
            }
          });
        }
        if (radio) {
          radio.checked = true;
          lastRadio = radio;
          const colorLabel = selector.querySelector('.js-color-text, .option-selector__label-value');
          if (colorLabel) {
            colorLabel.textContent = opt.val;
          }
        }
      }
    });

    // Update badges immediately
    updateBadges(picker);

    // Dispatch change event to Symmetry variant picker with variantId in detail
    const eventTarget = lastRadio || picker;
    eventTarget.dispatchEvent(new CustomEvent('change', {
      bubbles: true,
      cancelable: true,
      detail: { variantId: variantId }
    }));
  });

  // 2. LISTEN FOR VARIANT CHANGE (dispatched by Symmetry when user clicks any option or loads new section)
  document.addEventListener('on:variant:change', function(e) {
    const variant = e.detail?.variant;
    const picker = e.target.closest?.('variant-picker') || document.querySelector('variant-picker');
    updateBadges(picker);
    updateChipsState(picker, variant?.id);
  });

  // 3. LISTEN FOR REGULAR CHANGE ON OPTION INPUTS
  document.addEventListener('change', function(e) {
    if (e.target.matches?.('input.opt-btn') || e.target.closest?.('variant-picker')) {
      const picker = e.target.closest?.('variant-picker') || document.querySelector('variant-picker');
      setTimeout(() => {
        updateBadges(picker);
        updateChipsState(picker);
      }, 10);
    }
  });

  // 4. MUTATION OBSERVER: Automatically sync whenever Symmetry replaces DOM nodes
  let updateTimeout = null;
  const debouncedUpdate = () => {
    clearTimeout(updateTimeout);
    updateTimeout = setTimeout(() => {
      const picker = document.querySelector('variant-picker');
      if (picker) {
        updateBadges(picker);
        updateChipsState(picker);
      }
    }, 25);
  };

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.addedNodes.length) {
        for (const n of m.addedNodes) {
          if (n.nodeType === 1 && (n.matches?.('variant-picker, [data-dynamic-variant-content]') || n.querySelector?.('variant-picker'))) {
            debouncedUpdate();
            return;
          }
        }
      }
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  // 5. Initial run on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      updateBadges();
      updateChipsState();
    });
  } else {
    updateBadges();
    updateChipsState();
  }
})();
